import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import QrScanner from "../components/QrScanner";

function StatCard({ label, value, accent }) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wider text-fh-gray font-semibold">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeRentals, setActiveRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [searchError, setSearchError] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [items, rentals] = await Promise.all([
          api.listItems(),
          api.listRentals({ status: "active", limit: 10 }),
        ]);
        const counts = items.reduce((acc, it) => {
          acc[it.status] = (acc[it.status] || 0) + 1;
          return acc;
        }, {});
        setStats({
          total: items.length,
          available: counts.available || 0,
          rented: counts.rented || 0,
          maintenance: counts.maintenance || 0,
        });
        setActiveRentals(rentals);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSearchSubmit = async (e) => {
    e.preventDefault();
    setSearchError(null);
    const q = code.trim();
    if (!q) return;

    // Direkter RNT-Code → direkt aufrufen
    if (/^RNT-[A-Z0-9]+$/i.test(q)) {
      const id = q.toUpperCase().startsWith("RNT-") ? q.toUpperCase() : `RNT-${q.toUpperCase()}`;
      try {
        await api.getRental(id);
        navigate(`/rentals/${id}`);
        return;
      } catch {
        setSearchError("Kein Verleih mit diesem Code gefunden.");
        return;
      }
    }
    // Sonst: zur Liste mit Suche
    navigate(`/rentals?q=${encodeURIComponent(q)}`);
  };

  const onScanRentalCode = async (decoded) => {
    if (scanBusy) return;
    setScanBusy(true);
    setScanError(null);
    try {
      const id = parseRentalId(decoded);
      if (!id) {
        setScanError(`Kein gültiger Verleih-Code: „${decoded}"`);
        return;
      }
      await api.getRental(id);
      setScanOpen(false);
      navigate(`/rentals/${id}`);
    } catch (err) {
      setScanError(err.message || "Verleih nicht gefunden");
    } finally {
      setTimeout(() => setScanBusy(false), 700);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Hallo, {user?.full_name?.split(" ")[0]} 👋</h1>
        <p className="text-fh-gray mt-1">
          Frag den Kunden nach seinem Code oder durchsuche die Verleihe.
        </p>
      </div>

      {/* Code-Suche — Hauptaktion */}
      <div className="card p-5 border-l-4 border-fh-red">
        <h2 className="font-semibold mb-3">Verleih öffnen</h2>
        <form onSubmit={onSearchSubmit} className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="RNT-XXXXXXXX  oder  Name / Email"
            className="input-field font-mono text-lg flex-1"
            autoFocus
          />
          <button type="submit" className="btn-primary">🔎 Suchen</button>
          <button type="button" onClick={() => { setScanError(null); setScanOpen(true); }} className="btn-secondary">
            📷 Scannen
          </button>
        </form>
        {searchError && (
          <p className="text-sm text-fh-red-dark mt-2">{searchError}</p>
        )}
        <p className="text-xs text-fh-gray mt-3">
          Kunde hat noch keinen Code? <Link to="/rental/new" className="underline">Manuell anlegen</Link>
        </p>
      </div>

      {scanOpen && (
        <Modal
          open
          title="Verleih-Code scannen"
          onClose={() => setScanOpen(false)}
          size="md"
        >
          <div className="space-y-3">
            <p className="text-sm text-fh-gray">QR vom Kunden in den Rahmen halten.</p>
            <div className="max-w-xs mx-auto">
              <QrScanner onScan={onScanRentalCode} paused={scanBusy} onError={() => setScanError("Kamera nicht verfügbar")} />
            </div>
            {scanError && (
              <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded text-center">{scanError}</div>
            )}
          </div>
        </Modal>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Inventar gesamt" value={loading ? "…" : stats?.total ?? 0} />
        <StatCard label="Verfügbar" value={loading ? "…" : stats?.available ?? 0} accent="text-green-600" />
        <StatCard label="Verliehen" value={loading ? "…" : stats?.rented ?? 0} accent="text-fh-red" />
        <StatCard label="In Wartung" value={loading ? "…" : stats?.maintenance ?? 0} accent="text-yellow-600" />
      </div>

      <div className="card">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold">Aktive Verleihe</h2>
          <Link to="/rentals" className="text-sm text-fh-red hover:underline">Alle ansehen →</Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-fh-gray">Lade…</div>
        ) : activeRentals.length === 0 ? (
          <div className="p-8 text-center text-fh-gray">Aktuell keine aktiven Verleihe.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {activeRentals.map((r) => (
              <li key={r.rental_id}>
                <Link
                  to={`/rentals/${r.rental_id}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-fh-gray-light transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {r.first_name} {r.last_name}
                    </div>
                    <div className="text-xs text-fh-gray font-mono">
                      {r.rental_id} · {r.equipment_associations?.length ?? 0} Items
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function parseRentalId(decoded) {
  if (!decoded) return null;
  const direct = decoded.trim().toUpperCase().match(/RNT-[A-Z0-9]+/);
  if (direct) return direct[0];
  try {
    const url = new URL(decoded);
    const m = url.pathname.toUpperCase().match(/RNT-[A-Z0-9]+/);
    if (m) return m[0];
  } catch {
    /* not a URL */
  }
  return null;
}
